import React, { useState } from 'react';

const API = 'https://mizar-api.vercel.app';

function outcomeLabel(value) {
  if (value === 'TP') return 'True Positive';
  if (value === 'FP') return 'False Positive';
  if (value === 'TN') return 'True Negative';
  if (value === 'FN') return 'False Negative';
  return value || 'Pending';
}

export default function VerifiedOutcomesShell() {
  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [loading, setLoading] = useState(false);
  const [outcomes, setOutcomes] = useState([]);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);

  const totalVerified = Number(meta?.total_verified || 0);
  const pending = Number(meta?.pending || 0);

  const fetchOutcomes = async () => {
    setLoading(true);
    setError(null);

    try {
      const res = await fetch(`${API}/v1/outcomes`, {
        headers: {
          Authorization: `Bearer ${apiKey.trim()}`
        }
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.detail || `Request failed with ${res.status}`);
      }

      setOutcomes(data.outcomes || []);
      setMeta(data.meta || null);
    } catch (err) {
      setError(err.message || 'Failed to load verified outcomes.');
    } finally {
      setLoading(false);
    }
  };

  const tp = outcomes.filter((o) => o.prediction_outcome === 'TP').length;
  const fp = outcomes.filter((o) => o.prediction_outcome === 'FP').length;
  const precisionDenominator = tp + fp;
  const precision =
    totalVerified > 0 && precisionDenominator > 0
      ? Math.round((tp / precisionDenominator) * 100)
      : null;

  return (
    <div
      className="glass-card"
      style={{
        padding: '1.75rem',
        marginTop: '2rem',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: '1rem',
          alignItems: 'flex-start',
          marginBottom: '1rem',
          flexWrap: 'wrap'
        }}
      >
        <div>
          <h3
            style={{
              fontFamily: 'Manrope, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              marginBottom: '0.35rem'
            }}
          >
            Verified Outcomes
          </h3>

          <p
            style={{
              color: 'var(--text-secondary)',
              fontSize: '0.9rem',
              lineHeight: 1.6,
              margin: 0
            }}
          >
            Audited t+7 verification results for eligible v3 decisions.
          </p>
        </div>

        <span
          style={{
            border: '1px solid rgba(200, 123, 47, 0.45)',
            background: 'rgba(200, 123, 47, 0.1)',
            color: '#C87B2F',
            borderRadius: '999px',
            padding: '0.35rem 0.75rem',
            fontSize: '0.75rem',
            fontWeight: 700
          }}
        >
          {totalVerified > 0 ? 'Verification Active' : 'Verification Pending'}
        </span>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
          gap: '1rem',
          marginBottom: '1.25rem'
        }}
      >
        <div className="stat-card">
          <div className="stat-label">Verified rows</div>
          <div className="stat-value">{totalVerified > 0 ? totalVerified : 'Pending'}</div>
          <div className="stat-sublabel">Clean v3 eligible population</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Pending eligible</div>
          <div className="stat-value">{pending.toLocaleString()}</div>
          <div className="stat-sublabel">Awaiting t+7 verification</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Precision</div>
          <div className="stat-value">{precision === null ? 'Pending' : `${precision}%`}</div>
          <div className="stat-sublabel">Hidden until verified outcomes exist</div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: '0.75rem', marginBottom: '1.25rem' }}>
        <label style={{ fontWeight: 700, fontSize: '0.85rem' }}>API Key</label>

        <div
          style={{
            display: 'flex',
            gap: '0.75rem',
            flexWrap: 'wrap',
            width: '100%',
            maxWidth: '100%'
          }}
        >
          <input
            type={showKey ? 'text' : 'password'}
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder="Enter your MIZAR API key"
            style={{
              flex: '1 1 280px',
              width: '100%',
              minWidth: 0,
              maxWidth: '100%',
              padding: '0.85rem',
              borderRadius: '8px',
              border: '1px solid var(--border-subtle)',
              background: 'var(--bg-primary)',
              color: 'var(--text-primary)'
            }}
          />

          <button className="btn-secondary" type="button" onClick={() => setShowKey((v) => !v)}>
            {showKey ? 'Hide' : 'Show'}
          </button>

          <button
            className="btn-primary"
            type="button"
            onClick={fetchOutcomes}
            disabled={!apiKey.trim() || loading}
          >
            {loading ? 'Loading...' : 'Load outcomes'}
          </button>
        </div>
      </div>

      {error && (
        <div
          style={{
            border: '1px solid rgba(239, 107, 107, 0.45)',
            background: 'rgba(239, 107, 107, 0.08)',
            color: '#EF6B6B',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.85rem'
          }}
        >
          {error}
        </div>
      )}

      {meta && totalVerified === 0 && (
        <div
          style={{
            border: '1px solid rgba(200, 123, 47, 0.35)',
            background: 'rgba(200, 123, 47, 0.08)',
            color: 'var(--text-primary)',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            lineHeight: 1.6
          }}
        >
          Awaiting first v3 verification window. Expected meaningful verification population after ~15 May 2026.
          <br />
          Pending eligible decisions: <strong>{pending.toLocaleString()}</strong>
        </div>
      )}

      <div
        style={{
          overflowX: 'auto',
          width: '100%',
          maxWidth: '100%',
          WebkitOverflowScrolling: 'touch'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ color: 'var(--text-secondary)', textAlign: 'left' }}>
              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>Route</th>
              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>Score</th>
              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>Outcome</th>
              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>Price change</th>
              <th style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>Verified</th>
            </tr>
          </thead>

          <tbody>
            {totalVerified > 0 ? (
              outcomes.map((row) => {
                const d = row.user_decisions || {};
                return (
                  <tr key={row.decision_id}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>
                      {d.origin_iata} → {d.destination_iata}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>
                      {Number(d.regret_risk_score || 0).toFixed(3)}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>
                      {outcomeLabel(row.prediction_outcome)}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>
                      {row.price_change_pct == null ? '—' : `${Number(row.price_change_pct).toFixed(1)}%`}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)' }}>
                      {row.verification_timestamp
                        ? new Date(row.verification_timestamp).toLocaleDateString('en-GB')
                        : '—'}
                    </td>
                  </tr>
                );
              })
            ) : (
              [1, 2, 3].map((n) => (
                <tr key={n}>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Pending route</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Pending</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>Awaiting t+7</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>—</td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid var(--border-subtle)', color: 'var(--text-muted)' }}>After 15 May</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
